import './index.css'
import * as React from 'react'
import type { DragLayerMonitor } from 'react-dnd'
import { DragLayer } from 'react-dnd'
import { connect } from 'react-redux'
import type { State } from 'types/redux'
import type { ShortlistTrackItem } from 'types/shortlist'
import {
  getAllSelectedItemsCount,
  getSelectedItemsForPreview,
} from '@disco/state-management/selectors/selection'
import ItemsDragPreview from 'components/common/drag/items-drag-preview'

type DragItem = Partial<ShortlistTrackItem> & {
  isPartOfMultipleSelection?: boolean
  preview?: React.ReactNode
  width?: number
  height?: number
  [key: string]: any
}

function getItemStyles(props: Props) {
  const { initialOffset, currentOffset, clientOffset, item } = props

  if (!initialOffset || !currentOffset) {
    return {
      display: 'none',
    }
  }

  const offset = clientOffset || currentOffset
  const { x, y } = offset
  return {
    transform: `translate(${x}px, ${y}px)`,
  }
}

type StateProps = {
  selectedItemsCount: number
  previewItems: Array<ShortlistTrackItem>
}

function mapStateToProps(state: State): StateProps {
  return {
    selectedItemsCount: getAllSelectedItemsCount(state),
    previewItems: getSelectedItemsForPreview(state),
  }
}

type XYCoord = {
  x: number
  y: number
}

type DragLayerProps = {
  item: DragItem | null
  itemType: string | symbol | null
  initialOffset: XYCoord | null
  currentOffset: XYCoord | null
  clientOffset: XYCoord | null
  isDragging: boolean
}

type Props = DragLayerProps & StateProps

type ComponentState = {
  itemsToRender: Array<ShortlistTrackItem>
}

const AUTO_SCROLL_EDGE_SIZE = 100
const AUTO_SCROLL_MAX_SPEED = 20

export class CustomDragLayer extends React.Component<Props, ComponentState> {
  clientOffset: XYCoord | null = null
  autoScrollFrame: number | null = null

  constructor(props: Props) {
    super(props)
    this.state = {
      itemsToRender: [],
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    const selectionChanged = this.props.previewItems !== nextProps.previewItems
    const isDraggingChanged = this.props.isDragging !== nextProps.isDragging

    if (selectionChanged) {
      this.setState({
        itemsToRender: nextProps.previewItems,
      })
    }

    this.clientOffset = nextProps.clientOffset

    if (isDraggingChanged) {
      if (document?.body) {
        document.body.style.cursor = nextProps.isDragging ? 'grabbing' : ''
      }

      if (nextProps.isDragging) {
        this.startAutoScroll()
      } else {
        this.stopAutoScroll()
      }
    }
  }

  componentWillUnmount() {
    this.stopAutoScroll()
  }

  startAutoScroll = () => {
    if (this.autoScrollFrame !== null) return

    const tick = () => {
      this.autoScrollTick()
      this.autoScrollFrame = requestAnimationFrame(tick)
    }

    this.autoScrollFrame = requestAnimationFrame(tick)
  }

  stopAutoScroll = () => {
    if (this.autoScrollFrame !== null) {
      cancelAnimationFrame(this.autoScrollFrame)
      this.autoScrollFrame = null
    }
  }

  autoScrollTick = () => {
    if (!this.clientOffset) return

    const { y } = this.clientOffset
    const { innerHeight } = window
    let scrollBy = 0

    if (y < AUTO_SCROLL_EDGE_SIZE) {
      scrollBy = -Math.ceil(
        ((AUTO_SCROLL_EDGE_SIZE - y) / AUTO_SCROLL_EDGE_SIZE) *
          AUTO_SCROLL_MAX_SPEED,
      )
    } else if (y > innerHeight - AUTO_SCROLL_EDGE_SIZE) {
      scrollBy = Math.ceil(
        ((y - (innerHeight - AUTO_SCROLL_EDGE_SIZE)) / AUTO_SCROLL_EDGE_SIZE) *
          AUTO_SCROLL_MAX_SPEED,
      )
    }

    if (scrollBy !== 0) {
      window.scrollBy(0, scrollBy)
    }
  }

  render() {
    const { item, itemType, isDragging } = this.props
    if (!isDragging) return null
    let dragPreview

    if (item?.isPartOfMultipleSelection) {
      dragPreview = (
        <ItemsDragPreview
          type={itemType}
          item={item}
          selectedItemsCount={this.props.selectedItemsCount}
          previewItems={this.props.previewItems}
        />
      )
    } else if (item?.preview) {
      dragPreview = item?.preview
    }

    if (item?.width) {
      dragPreview = (
        <div
          className="Draggable-preview"
          style={{
            // Don't constrain width for custom previews
            width: item?.preview ? 'auto' : item?.width,
          }}
        >
          {dragPreview}
        </div>
      )
    }

    return (
      <div className="CustomDragLayer">
        <div style={getItemStyles(this.props)}>{dragPreview}</div>
      </div>
    )
  }
}

const dragLayer = DragLayer((monitor: DragLayerMonitor) => ({
  item: monitor.getItem() as DragItem | null,
  itemType: monitor.getItemType(),
  initialOffset: monitor.getInitialSourceClientOffset(),
  currentOffset: monitor.getSourceClientOffset(),
  clientOffset: monitor.getClientOffset(),
  isDragging: monitor.isDragging(),
}))(CustomDragLayer)

export default connect(mapStateToProps)(dragLayer)
